import React, { FunctionComponent, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import * as vp from 'vopay';
import useFetchPartnerAccoutPermissionFields from '@/hooks/useApi/useFetchPartnerAccoutPermissionFields';
import usePostPartnerAccountPermissions from '@/hooks/useApi/usePostPartnerAccountPermissions';
import usePermissionUpdateDialogue from '@/hooks/useDialogues/usePermissionUpdate.dialogue';
import { usePartnerAccounts } from '@/providers/PartnerAccounts.provider';
import { PendingDataProvider } from '@/providers/PendingData.provider';
import PermissionsForm from './PermissionsForm';
import PermissionsReview from './PermissionsReview';

import type { AccountsHandlers, PermissionsHandlers } from '../types';

const { Box, SubTitle, BackButton } = vp;

type PermissionsProps = {
  handlers: AccountsHandlers;
};

type FC = FunctionComponent<PermissionsProps>;

const Permissions: FC = ({ handlers: _handlers }: PermissionsProps) => {
  const reviewRef = React.createRef<HTMLDivElement>();
  const { accountId } = useParams<{ accountId: string }>();
  const [reviewing, setReviewing] = useState(false);
  const [groupedPermissionFields, setGroupedPermissionFields] = useState<
    Record<string, vp.PartnerAccountPermissionResponse[]>
  >({});
  const [defaultPermissions, setDefaultPermissions] = useState<any>({});
  const [permissionsResponse, postPermissions] = usePostPartnerAccountPermissions({});
  const { fetchAccountPermissionFields } = useFetchPartnerAccoutPermissionFields();
  const { showDialogue: showPermissionsDialogue } = usePermissionUpdateDialogue(permissionsResponse);
  const { refreshPartnerAccounts } = usePartnerAccounts();
  const [originalPermissions, setOriginalPermissions] = useState<Record<string, any>>({});

  useEffect(() => {
    if (!reviewing || !reviewRef.current) return;

    reviewRef.current.scrollIntoView({
      behavior: 'smooth',
      block: 'start',
    });
  }, [reviewing]);

  const handlers: PermissionsHandlers = {
    initializePermissions: (accountPermissions: vp.AccountPermissions) => {
      _handlers.initializePermissions(accountPermissions);
    },

    onPermissionChange: (permissionId: string, updatedValue: string | boolean) => {
      _handlers.onPermissionChange(permissionId, updatedValue);
    },

    onPermissionSave: (permissionsData: vp.AccountPermissions) => {
      handlers.initializePermissions(permissionsData);

      // Create object to hold only changed values
      const changedValues = Object.entries(permissionsData)
        .filter(hasChanged)
        .reduce((acc, [key, val]) => {
          return {
            ...acc,
            [key]: val,
          };
        }, {});

      function hasChanged([key, val]: [string, any]) {
        return originalPermissions[key] !== val;
      }

      const params = {
        VopayAccountID: accountId,
        ...changedValues,
      };

      // Pass the params to the postPermissions function
      postPermissions(params);
      setReviewing(false);
    },

    onReview: (accountPermissions: vp.AccountPermissions) => {
      // Create a deep copy of the accountPermissions to ensure it's not modified later
      setDefaultPermissions(JSON.parse(JSON.stringify(accountPermissions)));
      setReviewing(true);
    },
  };

  useEffect(() => {
    initStuff();

    async function initStuff() {
      const permissionFieldsResponse = await fetchAccountPermissionFields({ VopayAccountID: accountId });

      if (permissionFieldsResponse?.Success) {
        // Get fields from response
        const fields = permissionFieldsResponse.PermissionValues;

        // Ensure fields is a proper array
        const fieldsArray: vp.PartnerAccountPermissionResponse[] = Array.isArray(fields)
          ? fields
          : Object.values(fields);

        // Create default values mapping
        const defaultValuesMap = fieldsArray.reduce(
          (acc: Record<string, any>, field) => {
            const { Permission, Value } = field;

            acc[Permission] = Value;
            return acc;
          },
          {} as Record<string, any>,
        );

        // Set default permissions with the extracted values
        setDefaultPermissions({ ...defaultValuesMap });

        // Store the original values separately - never to be modified
        setOriginalPermissions({ ...defaultValuesMap });

        // Group by section
        const groupedBySection = fieldsArray.reduce(
          (acc: Record<string, vp.PartnerAccountPermissionResponse[]>, field) => {
            const section = field.Section || 'other';
            if (!acc[section]) {
              acc[section] = [];
            }
            acc[section].push(field);
            return acc;
          },
          {},
        );

        // Within each section, sort by type priority and then alphabetically
        Object.keys(groupedBySection).forEach((section) => {
          groupedBySection[section].sort((a, b) => {
            // Define type priority ranking (higher = more important)
            const getTypePriority = (type: string): number => {
              switch (type) {
                case 'boolean':
                  return 4;
                case 'number':
                  return 3;
                case 'text':
                case 'string':
                  return 2;
                case 'enum':
                case 'select':
                  return 1;
                default:
                  return 0;
              }
            };

            const priorityA = getTypePriority(a.Type);
            const priorityB = getTypePriority(b.Type);

            // Sort by priority first
            if (priorityA !== priorityB) {
              return priorityB - priorityA; // Higher priority first
            }

            // If same type, sort alphabetically by label
            return a.Label.localeCompare(b.Label);
          });
        });

        // Set the grouped permissions directly without flattening
        setGroupedPermissionFields(groupedBySection);

        // Initialize permissions with the extracted default values
        handlers.initializePermissions({ ...defaultValuesMap });
      }
    }
  }, []);

  useEffect(() => {
    if (permissionsResponse.success) {
      handlers.initializePermissions(defaultPermissions);
      refreshPartnerAccounts({
        VopayAccountID: accountId,
      });
    }
    if (permissionsResponse.success || permissionsResponse.errorMessage) {
      showPermissionsDialogue();
    }
  }, [permissionsResponse]);

  return (
    <div ref={reviewRef}>
      {reviewing && (
        <Box mb="lg" jc="fs" ai="center" gap="common">
          <BackButton
            variant="arrow"
            backButtonEnabled
            onClick={() => {
              setReviewing(false);
            }}
          />
          <SubTitle subTitle="Review and Save" />
        </Box>
      )}
      <PendingDataProvider>
        <div>
          {reviewing ? (
            <PermissionsReview
              values={defaultPermissions}
              onPermissionSave={handlers.onPermissionSave}
              permissionRows={groupedPermissionFields}
            />
          ) : (
            <PermissionsForm
              accountPermissions={defaultPermissions}
              handlers={handlers}
              permissionRows={groupedPermissionFields}
            />
          )}
        </div>
      </PendingDataProvider>
    </div>
  );
};

export default Permissions;
